import { RICH_TEXT_LINK_REL, RICH_TEXT_LINK_SCHEMES } from './rich-text.js';
import { MENTION_TRIGGER, isValidRef, parseMentions, type MentionKind } from './mention-token.js';

/**
 * CAOS-686 — how a conversation message's body is read: a deliberately small markdown, turned
 * straight into nodes.
 *
 * Shared code, not a component — it registers no custom element. `<caos-message-bubble>` and
 * `<caos-thread-panel>` both draw bodies, and two renderers would eventually disagree about what a
 * message may contain, which is the one thing about a message body that must have a single answer.
 *
 * ── WHY MARKDOWN AT ALL ──────────────────────────────────────────────────────────────────────
 * People type `**like this**` in a message whether or not anything renders it, and a surface that
 * does not shows the asterisks. The reference conversation surface reached the same conclusion from
 * the other direction: its bug report was "markdown rendered as raw text". So the question is not
 * whether to render it but how much, and the answer here is: the marks people actually type in a
 * message, and nothing else.
 *
 * ── NODES, NEVER A STRING ────────────────────────────────────────────────────────────────────
 * A message body is written by whoever could post a message, which is everybody. This renderer
 * therefore never builds HTML text and never hands a string to anything that parses HTML — it walks
 * the source and BUILDS elements, setting `textContent` for every piece of prose. There is no
 * second parse for a crafted string to be read differently by, which is the same property
 * `rich-text.ts` states for the rich Text kind and the reason it parses into an inert template
 * rather than into the document.
 *
 * Two consequences worth stating because they look like omissions:
 *   • Raw HTML in the source is NOT html. `<b>x</b>` in a message is the text `<b>x</b>`, drawn as
 *     typed. A conversation is prose, and there is no authoring surface behind it that would make
 *     inline markup a thing somebody meant.
 *   • A link's address goes through {@link RICH_TEXT_LINK_SCHEMES} — the SAME allow-list the rich
 *     Text kind uses, imported rather than restated, so there is one answer in this library to
 *     "what may a user-supplied address point at". A refused address keeps its text and loses its
 *     link, which is also what the rich reader does. Every kept link carries
 *     {@link RICH_TEXT_LINK_REL} and opens in a new context: a message is not the page, and
 *     following a link out of one should not take the conversation with it.
 *
 * ── WHAT IT DOES NOT DO ──────────────────────────────────────────────────────────────────────
 * No images, no footnotes, no inline HTML, no nested lists, no reference links, no setext
 * headings. Each is absent because a message is a sentence or two, and half-supporting a
 * construction is worse than not offering it: the author sees their syntax survive in one place and
 * not another. Tables ARE supported, because the reference surface found them genuinely used — a
 * reply that lays three numbers beside each other — and because a table that renders as pipes is
 * unreadable rather than merely plain.
 *
 * ── MENTIONS ARE DRAWN, AND ARE NOT LINKS (CAOS-689) ─────────────────────────────────────────
 * A stored mention is `<@person:…|Morgan Hale>`. Drawn as typed it is unreadable, which is the
 * whole reason it has to be handled here rather than left to the surface: a body renderer that
 * ignored the grammar would show every mention as its own machinery.
 *
 * It becomes a MARKED SPAN, not an anchor, and that is a decision rather than an omission. Where a
 * mention goes when it is followed depends on the reader — a record they cannot see resolves to
 * nothing, and the platform's rule for notification deep links is that the target is resolved WHEN
 * FOLLOWED in the recipient's access context, never pre-authorized. This library also has no
 * routing: it does not know what a record's address is on the surface it is embedded in. So the
 * span carries the kind and the reference on `data-` attributes and a surface that HAS routing
 * makes it clickable. A library that invented an address would invent a wrong one.
 *
 * The trigger character is drawn back on — the label is stored without it, and `Morgan Hale` in
 * the middle of a sentence does not read as a mention.
 *
 * A mention whose reference is malformed is NOT a mention: it stays in the message as the
 * characters somebody typed, the same answer this file already gives a link whose address is
 * refused. Two parsers agree on that rule — this one and the kernel's — and a fixture both suites
 * read is what keeps them agreeing.
 *
 * ── A PERSON WHO HAS LEFT IS DRAWN AS HAVING LEFT (CAOS-455) ─────────────────────────────────
 * The kernel's resolver exists so that "a mention of somebody who has gone must be able to say so
 * rather than name them as if they were still here" — and until this, nothing drew the difference,
 * so it could not. {@link MessageBodyOptions} is how a surface says who has gone; everything about
 * why it is phrased as ABSENCE rather than presence is on that interface.
 *
 * Only a `person` is ever marked. A record mention is not resolved before drawing, for the reason
 * the kernel's resolver gives: a mention that vanished or greyed for a reader who cannot open the
 * record would silently change what somebody wrote, and refusing at the door is where that belongs.
 */

/** One block of a parsed body, in the order it was written. */
export type MessageBlock =
  | { kind: 'paragraph'; text: string }
  | { kind: 'heading'; level: 2 | 3; text: string }
  | { kind: 'blockquote'; text: string }
  | { kind: 'code'; language: string | null; text: string }
  | { kind: 'list'; ordered: boolean; items: string[] }
  | { kind: 'table'; header: string[]; rows: string[][] }
  | { kind: 'rule' };

const FENCE = /^\s*```(.*)$/;
const HEADING = /^(#{1,6})\s+(.*)$/;
const BLOCKQUOTE = /^>\s?(.*)$/;
const RULE = /^\s*([-*_])\s*(?:\1\s*){2,}$/;
const BULLET = /^\s*[-*+]\s+(.*)$/;
const ORDERED = /^\s*\d+[.)]\s+(.*)$/;
const TABLE_DIVIDER = /^\s*\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)*\|?\s*$/;

/**
 * Split a table row on its cell pipes, dropping the leading and trailing ones.
 *
 * A pipe INSIDE A MENTION is not a cell boundary (CAOS-455). The mention grammar separates a
 * reference from its label with `|` — `<@person:…|Morgan Lee>` — so splitting on every pipe shreds
 * every mention in a table into two cells: the reader gets `<@person:6f1c…` in one column and
 * `Morgan>` in the next, which is the machinery on show and the name detached from it. Tables and
 * mentions arrived in this renderer from different tickets and had never met.
 *
 * Protected only for a WELL-FORMED mention, found with the same parser the inline reader uses, so
 * the two cannot disagree about what counts. A malformed token is not a mention anywhere else in
 * this file, and its pipe splits like any other — which is also what keeps prose that merely
 * contains `<@` from swallowing a real cell boundary for the rest of the line.
 */
function tableCells(line: string): string[] {
  const trimmed = line.trim().replace(/^\|/, '').replace(/\|$/, '');
  if (!trimmed.includes('|')) return [trimmed.trim()];
  const protectedSpans = parseMentions(trimmed).map((t) => [t.start, t.end] as const);
  const inMention = (at: number): boolean => protectedSpans.some(([s, e]) => at >= s && at < e);
  const cells: string[] = [];
  let cell = '';
  for (let i = 0; i < trimmed.length; i += 1) {
    const ch = trimmed[i] ?? '';
    if (ch === '|' && !inMention(i)) {
      cells.push(cell.trim());
      cell = '';
      continue;
    }
    cell += ch;
  }
  cells.push(cell.trim());
  return cells;
}

/**
 * Parse a body into blocks. Exported separately from the drawing so the reading rules can be tested
 * as data — a test that can only assert on rendered DOM has to describe a paragraph by the element
 * it became, which stops being a statement about the markdown.
 */
export function parseMessageBody(source: string): MessageBlock[] {
  const lines = source.replace(/\r\n/g, '\n').split('\n');
  const blocks: MessageBlock[] = [];
  let paragraph: string[] = [];

  const flushParagraph = (): void => {
    if (paragraph.length === 0) return;
    blocks.push({ kind: 'paragraph', text: paragraph.join('\n') });
    paragraph = [];
  };

  for (let i = 0; i < lines.length; i += 1) {
    const line = lines[i] ?? '';

    // A fence runs to its closing fence, or to the end of the body — an unclosed fence is a code
    // block, not a failure. Somebody pasting a snippet and forgetting the closing marks should see
    // their snippet, not the rest of their message reinterpreted as prose around it.
    const fence = FENCE.exec(line);
    if (fence) {
      flushParagraph();
      const language = (fence[1] ?? '').trim() || null;
      const body: string[] = [];
      i += 1;
      while (i < lines.length && !FENCE.test(lines[i] ?? '')) {
        body.push(lines[i] ?? '');
        i += 1;
      }
      blocks.push({ kind: 'code', language, text: body.join('\n') });
      continue;
    }

    if (line.trim() === '') {
      flushParagraph();
      continue;
    }

    if (RULE.test(line)) {
      flushParagraph();
      blocks.push({ kind: 'rule' });
      continue;
    }

    const heading = HEADING.exec(line);
    if (heading) {
      flushParagraph();
      // Every heading level collapses to two, because a message is not a document: its own heading
      // levels are relative to nothing, and a message that opened at `h1` would outrank the page.
      const level = (heading[1] ?? '#').length <= 2 ? 2 : 3;
      blocks.push({ kind: 'heading', level, text: (heading[2] ?? '').trim() });
      continue;
    }

    const blockquote = BLOCKQUOTE.exec(line);
    if (blockquote) {
      flushParagraph();
      const body = [blockquote[1] ?? ''];
      while (i + 1 < lines.length && BLOCKQUOTE.test(lines[i + 1] ?? '')) {
        i += 1;
        body.push(BLOCKQUOTE.exec(lines[i] ?? '')?.[1] ?? '');
      }
      blocks.push({ kind: 'blockquote', text: body.join('\n') });
      continue;
    }

    const bullet = BULLET.exec(line);
    const ordered = ORDERED.exec(line);
    if (bullet || ordered) {
      flushParagraph();
      const isOrdered = !bullet;
      const items: string[] = [(bullet ?? ordered)?.[1] ?? ''];
      while (i + 1 < lines.length) {
        const next = lines[i + 1] ?? '';
        const nextItem = isOrdered ? ORDERED.exec(next) : BULLET.exec(next);
        if (!nextItem) break;
        items.push(nextItem[1] ?? '');
        i += 1;
      }
      blocks.push({ kind: 'list', ordered: isOrdered, items });
      continue;
    }

    // A table is a header row followed by a divider row. Checking the SECOND line is what stops an
    // ordinary sentence containing a pipe from becoming a one-column table.
    if (line.includes('|') && TABLE_DIVIDER.test(lines[i + 1] ?? '')) {
      flushParagraph();
      const header = tableCells(line);
      i += 1;
      const rows: string[][] = [];
      while (i + 1 < lines.length && (lines[i + 1] ?? '').includes('|')) {
        i += 1;
        rows.push(tableCells(lines[i] ?? ''));
      }
      blocks.push({ kind: 'table', header, rows });
      continue;
    }

    paragraph.push(line);
  }
  flushParagraph();
  return blocks;
}

/** Is this address one a message may link to? The same question the rich Text kind asks. */
export function isLinkableAddress(href: string): boolean {
  let url: URL;
  try {
    url = new URL(href);
  } catch {
    return false; // a relative address in a message has no page to be relative to
  }
  if (!(RICH_TEXT_LINK_SCHEMES as readonly string[]).includes(url.protocol)) return false;
  // A user name or password in an address is how one host is made to read as another.
  return url.username === '' && url.password === '';
}

interface InlineToken {
  kind: 'text' | 'code' | 'strong' | 'em' | 'strike' | 'link' | 'mention';
  text: string;
  href?: string;
  /** Mentions only: what is pointed at, and what it is. */
  ref?: string;
  mentionKind?: MentionKind;
}

const INLINE = new RegExp(
  [
    '`([^`]+)`', // 1 — code, first: nothing inside a code span is a mark
    '\\*\\*([^*]+)\\*\\*', // 2 — strong
    '__([^_]+)__', // 3 — strong
    '~~([^~]+)~~', // 4 — strike
    '\\*([^*]+)\\*', // 5 — em
    '_([^_]+)_', // 6 — em
    '\\[([^\\]]+)\\]\\(([^)\\s]+)\\)', // 7,8 — link
    '<((?:https?|mailto):[^>\\s]+)>', // 9 — autolink
    '((?:https?://|mailto:)[^\\s<>()]+)', // 10 — a bare address
    // 11,12,13 — a mention. LAST in the alternation and it costs nothing, because the alternation
    // is only ever tried at a position, and the only other alternative that can begin at `<` is
    // the autolink above, which requires a scheme. Placing it last also keeps every group number
    // ahead of it unchanged.
    //
    // Its label is deliberately NOT re-parsed for marks: it is somebody's name, and an asterisk in
    // a name is part of the name. Nothing inside the token is looked at again.
    '<@(person|record):([^|>\\s]+)\\|([^<>]*)>', // 11,12,13 — mention
  ].join('|'),
  'g',
);

/**
 * Split one run of prose into its inline pieces.
 *
 * Code first, and deliberately: a mark inside a code span is part of the code. Ordering the
 * alternation the other way would render `` `**not bold**` `` as bold inside a code pill, which is
 * the one place an author is certain they meant the characters themselves.
 */
export function parseInline(text: string): InlineToken[] {
  const out: InlineToken[] = [];
  let last = 0;
  INLINE.lastIndex = 0;
  for (let m = INLINE.exec(text); m !== null; m = INLINE.exec(text)) {
    if (m.index > last) out.push({ kind: 'text', text: text.slice(last, m.index) });
    if (m[1] !== undefined) out.push({ kind: 'code', text: m[1] });
    else if (m[2] !== undefined) out.push({ kind: 'strong', text: m[2] });
    else if (m[3] !== undefined) out.push({ kind: 'strong', text: m[3] });
    else if (m[4] !== undefined) out.push({ kind: 'strike', text: m[4] });
    else if (m[5] !== undefined) out.push({ kind: 'em', text: m[5] });
    else if (m[6] !== undefined) out.push({ kind: 'em', text: m[6] });
    else if (m[7] !== undefined && m[8] !== undefined) out.push({ kind: 'link', text: m[7], href: m[8] });
    else if (m[9] !== undefined) out.push({ kind: 'link', text: m[9], href: m[9] });
    else if (m[10] !== undefined) out.push({ kind: 'link', text: m[10], href: m[10] });
    else if (m[11] !== undefined && m[12] !== undefined) {
      const mentionKind = m[11] as MentionKind;
      const ref = m[12];
      // Malformed reference ⇒ not a mention. The characters stay, exactly as a refused link keeps
      // its text: a body must never show LESS than what was written.
      if (isValidRef(mentionKind, ref)) out.push({ kind: 'mention', text: m[13] ?? '', ref, mentionKind });
      else out.push({ kind: 'text', text: m[0] });
    }
    last = m.index + m[0].length;
  }
  if (last < text.length) out.push({ kind: 'text', text: text.slice(last) });
  return out;
}

/**
 * What a caller knows about the people a body names, beyond what the body itself says (CAOS-455).
 *
 * ── WHY ABSENCE AND NOT PRESENCE ──────────────────────────────────────────────────────────────
 * The polarity is load-bearing. `absentPeople` is a POSITIVE statement — "these people have gone"
 * — so a caller that resolved nobody says nothing and every mention draws as it always did. The
 * other way round, a `presentPeople` set would make every id a caller forgot to resolve, or
 * resolved after the first paint, draw as somebody who has left: a renderer inferring departure
 * from its own ignorance, and doing it to a name in a sentence somebody else wrote.
 *
 * Who is absent is not a question this library can answer — it has no connection and no identity
 * model. The surface asks `mentions resolve`, which answers about every id it was given precisely
 * so that "not here" and "we did not look" cannot be confused, and passes the gone ones down.
 */
export interface MessageBodyOptions {
  /**
   * Principal ids of mentioned people who are no longer in this org. Matched case-insensitively,
   * because an id is a uuid and a caller should not have to care which case it arrived in.
   */
  absentPeople?: Iterable<string>;
}

/** The options as the renderer uses them: one lower-cased set, built once per render. */
interface ResolvedBodyOptions {
  absent: ReadonlySet<string>;
}

const NO_ABSENT: ResolvedBodyOptions = { absent: new Set() };

function resolveOptions(opts: MessageBodyOptions | undefined): ResolvedBodyOptions {
  if (!opts?.absentPeople) return NO_ABSENT;
  return { absent: new Set([...opts.absentPeople].map((id) => id.toLowerCase())) };
}

/** Build the nodes for one run of prose, into the document the message will be shown in. */
function inlineNodes(doc: Document, text: string, opts: ResolvedBodyOptions = NO_ABSENT): Node[] {
  return parseInline(text).map((token) => {
    if (token.kind === 'text') return doc.createTextNode(token.text);
    if (token.kind === 'link') {
      const href = token.href ?? '';
      if (!isLinkableAddress(href)) {
        // The text survives; only the link is refused. A refused address must not remove what the
        // person wrote, or the message says less than they typed.
        return doc.createTextNode(token.text);
      }
      const a = doc.createElement('a');
      a.href = href;
      a.rel = RICH_TEXT_LINK_REL;
      a.target = '_blank';
      a.className = 'md-link';
      a.textContent = token.text;
      return a;
    }
    if (token.kind === 'mention') {
      const kind = token.mentionKind ?? 'person';
      const ref = token.ref ?? '';
      const span = doc.createElement('span');
      span.className = 'md-mention';
      span.dataset['mentionKind'] = kind;
      span.dataset['mentionRef'] = ref;
      // A person the caller told us has gone is drawn as gone (CAOS-455). The LABEL is untouched:
      // the sentence was written around the name the writer chose, and the current name of somebody
      // who has left is not ours to substitute. Only `person` is ever marked — a record mention is
      // never resolved before drawing, because a mention that vanished for a reader who cannot open
      // the record would silently change what somebody wrote.
      if (kind === 'person' && opts.absent.has(ref.toLowerCase())) {
        span.dataset['mentionPresent'] = 'false';
        // Said in words as well as in styling. A reader using a screen reader, or one who cannot
        // tell two shades of the same colour apart, is exactly the reader who needs to know the
        // person being named is no longer here.
        span.title = 'No longer in this workspace';
      }
      // The trigger is drawn, never stored: `Morgan Hale` mid-sentence does not read as a mention.
      span.textContent = `${MENTION_TRIGGER}${token.text}`;
      return span;
    }
    const tag = token.kind === 'code' ? 'code' : token.kind === 'strong' ? 'strong' : token.kind === 'em' ? 'em' : 's';
    const el = doc.createElement(tag);
    if (token.kind === 'code') el.className = 'md-code';
    el.textContent = token.text;
    return el;
  });
}

/**
 * Draw a message body into a fragment, ready to be appended.
 *
 * `doc` is an argument because a component builds into its own shadow root's document, and a
 * function that reached for the global `document` would be wrong in exactly the environments this
 * library is tested in.
 */
export function renderMessageBody(
  source: string,
  doc: Document = document,
  opts?: MessageBodyOptions,
): DocumentFragment {
  const resolved = resolveOptions(opts);
  const frag = doc.createDocumentFragment();
  for (const block of parseMessageBody(source)) {
    switch (block.kind) {
      case 'rule':
        frag.append(doc.createElement('hr'));
        break;
      case 'heading': {
        const h = doc.createElement(`h${block.level}`);
        h.className = 'md-heading';
        h.append(...inlineNodes(doc, block.text, resolved));
        frag.append(h);
        break;
      }
      case 'code': {
        const pre = doc.createElement('pre');
        pre.className = 'md-pre';
        const code = doc.createElement('code');
        if (block.language) code.dataset['language'] = block.language;
        // textContent, always: a code block is the one place where what was typed is the whole of
        // what is meant, and it is also where somebody would try to smuggle markup.
        code.textContent = block.text;
        pre.append(code);
        frag.append(pre);
        break;
      }
      case 'blockquote': {
        const q = doc.createElement('blockquote');
        q.className = 'md-blockquote';
        q.append(...inlineNodes(doc, block.text, resolved));
        frag.append(q);
        break;
      }
      case 'list': {
        const list = doc.createElement(block.ordered ? 'ol' : 'ul');
        list.className = 'md-list';
        for (const item of block.items) {
          const li = doc.createElement('li');
          li.append(...inlineNodes(doc, item, resolved));
          list.append(li);
        }
        frag.append(list);
        break;
      }
      case 'table': {
        // The wrapper is not decoration: a table wider than the message must scroll inside the
        // message rather than widening it, or one pasted table pushes the whole conversation
        // sideways.
        const wrap = doc.createElement('div');
        wrap.className = 'md-table-wrap';
        const table = doc.createElement('table');
        table.className = 'md-table';
        const thead = doc.createElement('thead');
        const hr = doc.createElement('tr');
        for (const cell of block.header) {
          const th = doc.createElement('th');
          th.append(...inlineNodes(doc, cell, resolved));
          hr.append(th);
        }
        thead.append(hr);
        table.append(thead);
        const tbody = doc.createElement('tbody');
        for (const row of block.rows) {
          const tr = doc.createElement('tr');
          for (const cell of row) {
            const td = doc.createElement('td');
            td.append(...inlineNodes(doc, cell, resolved));
            tr.append(td);
          }
          tbody.append(tr);
        }
        table.append(tbody);
        wrap.append(table);
        frag.append(wrap);
        break;
      }
      default: {
        const p = doc.createElement('p');
        p.className = 'md-p';
        // A single newline inside a paragraph is a line break. In a message it always is: people
        // press Shift+Enter to start a new line and mean a new line.
        const parts = block.text.split('\n');
        parts.forEach((part, index) => {
          if (index > 0) p.append(doc.createElement('br'));
          p.append(...inlineNodes(doc, part, resolved));
        });
        frag.append(p);
      }
    }
  }
  return frag;
}

/** The stylesheet rules a surface drawing message bodies adopts. Tokens only, never a raw value. */
export const MESSAGE_BODY_CSS = `
  .md-p { margin: 0 0 var(--caos-space-2); line-height: 1.5; }
  .md-p:last-child { margin-bottom: 0; }
  .md-heading { margin: var(--caos-space-2) 0 var(--caos-space-1); font-size: var(--caos-text-body); font-weight: var(--caos-font-weight-label); }
  .md-heading:first-child { margin-top: 0; }
  .md-list { margin: 0 0 var(--caos-space-2); padding-inline-start: var(--caos-space-4); }
  .md-list:last-child { margin-bottom: 0; }
  .md-blockquote {
    margin: 0 0 var(--caos-space-2);
    padding-inline-start: var(--caos-space-2);
    border-inline-start: 2px solid var(--caos-color-border);
    color: var(--caos-color-text-muted);
  }
  .md-code {
    font-family: var(--caos-font-mono);
    font-size: 0.9em;
    background: var(--caos-color-surface-2);
    border: 1px solid var(--caos-color-border);
    border-radius: var(--caos-radius-sm);
    padding: 0 4px;
  }
  .md-pre {
    margin: 0 0 var(--caos-space-2);
    padding: var(--caos-space-2);
    background: var(--caos-color-surface-2);
    border: 1px solid var(--caos-color-border);
    border-radius: var(--caos-radius-sm);
    overflow-x: auto;
  }
  .md-pre code { font-family: var(--caos-font-mono); font-size: 0.9em; white-space: pre; }
  .md-link { color: var(--caos-color-accent); }
  /* Reads as a mention without pretending to be a link — no underline and no pointer, because
     nothing happens when it is clicked until a surface with routing makes something happen. */
  .md-mention {
    color: var(--caos-color-accent);
    background: var(--caos-color-surface-2);
    border-radius: var(--caos-radius-sm);
    padding: 0 3px;
    font-weight: var(--caos-font-weight-label);
  }
  /* Somebody who has left (CAOS-455). Loses the accent, because the accent is what says "this is a
     live reference to a colleague" — and keeps the label, because that is what the writer wrote.
     The strike-through carries the meaning where colour cannot: the muted colour alone would be a
     distinction two readers in a hundred cannot see, on a name in somebody else's sentence. */
  .md-mention[data-mention-present='false'] {
    color: var(--caos-color-text-muted);
    text-decoration: line-through;
    text-decoration-thickness: 1px;
  }
  .md-link:focus-visible { outline: none; box-shadow: var(--caos-focus-ring); }
  .md-table-wrap { margin: 0 0 var(--caos-space-2); overflow-x: auto; max-width: 100%; }
  .md-table { border-collapse: collapse; font-size: var(--caos-text-body); }
  .md-table th, .md-table td {
    border: 1px solid var(--caos-color-border);
    padding: 4px var(--caos-space-2);
    text-align: start;
  }
  .md-table th { background: var(--caos-color-surface-2); font-weight: var(--caos-font-weight-label); }
  hr { border: 0; border-top: 1px solid var(--caos-color-border); margin: var(--caos-space-2) 0; }
`;
